<template>
  <p-feature-flag
    name="Sales Reconciliation"
    redirect>
    <p-search-screen
        ref="searchScreen"
        title="Sales Reconciliation"
        api-destination="salesReconciliation"
        :filters="filters"
        :fields="fields"
        hide-search
        edit-key="salesReconciliationId"
        edit-button-label="Manage"
        >
        <template v-slot:actionButtons>
            <p-button
                variant="primary"
                @click="openCreateModal"
            >Create</p-button>
        </template>
        <template v-slot:belowSearch>
            <p-form>
                <b-alert show variant="info" class="mb-2">
                    Business dates are determined by each location's End of Accounting Day at the moment a day's
                    record is created. Changing the End of Accounting Day affects only newly created dates &mdash; existing
                    reconciliation dates and their totals are not changed.
                </b-alert>
                <b-row class="mt-4 me-2">
                    <p-checkbox
                        label="Open"
                        :checked="filters.open"
                        v-model="filters.open"
                    />
                    <p-checkbox
                        label="Posted"
                        class="ms-3"
                        v-model="filters.posted"
                    />
                    <p-checkbox
                        label="Out Of Balance"
                        class="ms-3"
                        :checked="filters.outOfBalance"
                        v-model="filters.outOfBalance"
                    />
                </b-row>
                <b-row class="mt-3 me-2 align-items-end">
                    <b-col cols="12" sm="6" lg="3">
                        <p-datepicker
                            label="Sales Date: From"
                            vid="beginDate"
                            v-model="beginDateModel"
                        />
                    </b-col>
                    <b-col cols="auto">
                        <p-datepicker
                            label="Sales Date: To"
                            v-model="endDateModel"
                            rules="dateGreaterOrEqual:@beginDate"
                            is-end-date
                        />
                    </b-col>
                </b-row>
            </p-form>
      </template>
    </p-search-screen>

    <p-modal
        name="createDateRangeModal"
        label="Create Sales Reconciliations"
        ref="createModal"
        size="lg"
        @ok.prevent="handleCreate"
    >
        <template v-slot:default>
            <p-form ref="createForm">
                <div class="container-fluid">
                    <p-date-range :date-range-options="rangeOptions" />
                    <b-alert :show="rangeTooLong" variant="warning" class="mt-2">
                        Date range cannot exceed 31 days.
                    </b-alert>
                </div>
            </p-form>
        </template>
        <template v-slot:modal-footer="{ ok, cancel }">
            <b-row>
                <b-col>
                    <p-button
                        variant="outline-primary"
                        :is-busy="isBusy"
                        @click="cancel"
                    >Cancel</p-button>
                    <p-button
                        variant="primary"
                        class="ms-2"
                        :is-busy="isBusy"
                        :disabled="rangeTooLong"
                        @click="ok"
                    >Create</p-button>
                </b-col>
            </b-row>
        </template>
    </p-modal>
  </p-feature-flag>
</template>

<script>
import axios from 'axios';
import SearchStorage from '@/components/mixins/SearchFilterStorage.js';
import DateOnlyPickerFilters from '@/components/mixins/DateOnlyPickerFilters.js';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import { dateOnlyFormatter, currencyFormatter } from '../../components/Common/Formatters.js';

export default {
  // DateOnlyPickerFilters must follow SearchStorage — it seeds from the filters that mixin restores.
  mixins: [SearchStorage, DateOnlyPickerFilters],
  data() {
    return {
      filterName: 'SalesReconciliationFilter',
      isBusy: false,
      locationId: null,
      rangeOptions: { fromDate: null, toDate: null },
      // Datepicker-bound models hold the store-tz ISO the picker emits; DateOnlyPickerFilters
      // translates between these and the YYYY-MM-DD filters below. The "To" picker carries
      // is-end-date, so its pairing seeds the end-of-day moment that picker emits.
      beginDateModel: null,
      endDateModel: null,
      datePickerFilters: [
        { model: 'beginDateModel', filter: 'beginDate' },
        { model: 'endDateModel', filter: 'endDate', isEndDate: true }
      ],
      filters: {
        automatic: "",
        perPage: 100,
        sortBy: 'salesDate',
        sortDesc: true,
        searchBy:'All',
        beginDate: null,
        endDate: null,
        open: true,
        posted: false,
        outOfBalance: true
      },
      fields: [
        {
          key: "locationName",
          sortable: true
        },
        {
          key: "salesDate",
          sortable: true,
          formatter: dateOnlyFormatter
        },
        {
          key: "revenueCollected",
          sortable: true,
          formatter: currencyFormatter
        },
        {
          key: "revenueBreakdown",
          sortable: true,
          formatter: currencyFormatter
        },
        {
          key: "revenueDifference",
          sortable: true,
          formatter: currencyFormatter
        },
        {
          key: "status",
          sortable: true
        }
      ]
    };
  },
  computed: {
    rangeDayCount() {
      const from = this.rangeOptions.fromDate;
      const to = this.rangeOptions.toDate;
      if (!from || !to) return 0;
      // Compare on yyyy-MM-dd (UTC midnight) so the difference is a clean whole-day count.
      const fromUtc = new Date(this.toDateParam(from));
      const toUtc = new Date(this.toDateParam(to));
      return Math.floor((toUtc - fromUtc) / 86400000) + 1;
    },
    rangeTooLong() {
      return this.rangeDayCount > 31;
    }
  },
  methods: {
    openCreateModal() {
      this.$refs.createModal.show();
    },
    async handleCreate() {
      if (!(await this.$refs.createForm.validate())) {
        return;
      }
      if (!this.rangeOptions.fromDate || !this.rangeOptions.toDate) {
        return;
      }
      const fromDate = this.toDateParam(this.rangeOptions.fromDate);
      const toDate = this.toDateParam(this.rangeOptions.toDate);
      this.isBusy = true;
      axios
        .post(`salesReconciliation/createForDateRange?locationId=${this.locationId}&fromDate=${fromDate}&toDate=${toDate}`)
        .then(response => {
          const { created, alreadyExisted, noTransactions } = response.data;
          const message = this.buildSummaryMessage(created, alreadyExisted, noTransactions);
          if (created > 0) {
            this.$appSuccess(message);
          } else {
            this.$appWarning(message);
          }
          this.$refs.createModal.hide();
          this.$refs.searchScreen.refreshSearchResults();
        })
        .finally(() => {
          this.isBusy = false;
        });
    },
    buildSummaryMessage(created, alreadyExisted, noTransactions) {
      const parts = [`${created} created`];
      if (alreadyExisted > 0) parts.push(`${alreadyExisted} already existed`);
      if (noTransactions > 0) parts.push(`${noTransactions} had no sales`);
      return `${parts.join(', ')}.`;
    }
  },
  async created() {
    this.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
  }
};
</script>
